export interface SchoolDef {
  readonly id: string;
  /** `launch` sects are in the first release; `later` ones are defined but not recruiting yet (docs/design/SECT_ROSTER_ORIGINAL.md). */
  readonly phase: 'launch' | 'later';
  readonly name: string;
  readonly motto: string;
  readonly desc: string;
  /** Title given to disciples on joining. */
  readonly discipleTitle: string;
  /** Martial arts taught, in learning order. The first is given on joining. */
  readonly arts: readonly string[];
  /** Internal art given on joining. */
  readonly neigong: string;
  /** Sprite key of the school uniform; `${uniform}Palm` is the unarmed variant. */
  readonly uniform: string;
  readonly master: { readonly name: string; readonly title: string; readonly sprite: string };
  /** Where the recruiter stands, relative to the plaza centre (tiles). */
  readonly post: { readonly dx: number; readonly dy: number };
  /**
   * Where its recruiter stands in a village's hall of sects (the map's `schools`
   * landmark, see systems/recruiters.ts), by its banner: tiles from the landmark.
   */
  readonly recruit: { readonly dx: number; readonly dy: number };
  readonly joinText: string;
  readonly memberText: string;
}

const list: SchoolDef[] = [
  {
    id: 'bamboo-tide',
    phase: 'launch',
    name: 'คลื่นไผ่',
    motto: 'คลื่นไม่สู้ฝั่ง คลื่นแค่กลับมา',
    desc: 'ศาลชาวประมงแห่งหมู่บ้านจวงซี ผู้เฝ้ากระบี่ไผ่ ช่างซ่อมอวน คนพายเรือ และผู้อ่านน้ำ ฝึกจังหวะสามจังหวะ ซัดเข้า ถอย แตกฝั่ง ให้ท่าแต่ละท่าเปิดทางท่าถัดไป',
    discipleTitle: 'ศิษย์สำนักคลื่นไผ่',
    arts: [],
    neigong: 'tide-rhythm',
    uniform: 'heroWudang',
    master: { name: 'ยายเหมย', title: 'ผู้เฝ้าศาลคลื่นไผ่', sprite: 'taoist' },
    post: { dx: 0, dy: 0 },
    recruit: { dx: 0, dy: 1 },
    joinText: 'คลื่นไผ่ไม่ได้สอนให้ชนะ แต่สอนให้ท่าหนึ่งเปิดทางท่าถัดไป หากเข้าสำนัก เจ้าจะได้ลมหายใจสามจังหวะทันที เจ้าจะเข้าสำนักหรือไม่',
    memberText: 'ศิษย์คลื่นไผ่ ซัดเข้า ถอย แตกฝั่ง อย่าลืมจังหวะ เปิดแผงสำนัก (J) เมื่อแต้มฝึกปรือพอ',
  },
  {
    id: 'mist-peak',
    phase: 'launch',
    name: 'ยอดหมอก',
    motto: 'อย่ายืนตรงที่ฝ่ามือจะลง',
    desc: 'ผู้เฝ้ากระบี่ลายเมฆา คนเก็บชา คนเลี้ยงแพะ และผู้อ่านทางลมบนที่สูง ไม่ใช่นักบวช ฝึกวิชาวงหมอก ถนัดการเคลื่อนที่ ดึง ผลัก และเลี่ยงแรง ชนะด้วยตำแหน่งและขอบลาน',
    discipleTitle: 'ศิษย์สำนักยอดหมอก',
    arts: ['breeze-sword', 'drift-fist', 'drift-sword'],
    neigong: 'mist-breath',
    uniform: 'heroWudang',
    master: { name: 'ผู้เฒ่าชิงซง', title: 'ผู้แทนสำนักยอดหมอก', sprite: 'taoist' },
    post: { dx: -3.3, dy: -1.2 },
    recruit: { dx: -7, dy: 1 },
    joinText:
      'ผู้ลงนามสัญญาเลือด ยอดหมอกไม่ได้สอนให้ชนะด้วยแรง แต่สอนให้ไม่ไปยืนตรงที่แรงจะลง หากเข้าสำนัก เจ้าจะได้รับกระบี่ไผ่ห่มหมอกและลมปราณยอดหมอกทันที ' +
      'ส่วนหมัดหมอกวนและกระบี่วงธาราต้องฝึกปรือแลกมาในแผงสำนัก (J) เจ้าจะเข้าสำนักหรือไม่',
    memberText:
      'ศิษย์ยอดหมอก จงจำไว้ว่าวงหมอกเริ่มจากหมัดแล้วจึงถึงกระบี่ เปิดแผงสำนัก (J) เพื่อเรียนวิชาขั้นต่อไป เมื่อแต้มฝึกปรือพอ',
  },
  {
    id: 'bell-stone',
    phase: 'launch',
    name: 'หินระฆัง',
    motto: 'ระฆังดังเพราะเนื้อแน่น คนยืนได้เพราะใจนิ่ง',
    desc: 'โรงหล่อระฆังของช่างบนภูเขาพันบันได ผู้เฝ้ากระบี่ระฆังเหล็ก ช่างหล่อ คนดึงเชือก และช่างปรับเสียง ฝึกกายให้แน่นดุจเนื้อระฆัง รับแรงสะสมเสียงก้อง แล้วปล่อยคลื่นกระแทกให้ศัตรูเซ',
    discipleTitle: 'ศิษย์สำนักหินระฆัง',
    arts: ['bell-fist', 'hum-palm', 'founder-sabre'],
    neigong: 'bell-body',
    uniform: 'heroShaolin',
    master: { name: 'ช่างใหญ่ฮุ่ยเหมิง', title: 'ผู้แทนสำนักหินระฆัง', sprite: 'monk' },
    post: { dx: 2.6, dy: -3.4 },
    recruit: { dx: -4.2, dy: 1 },
    joinText:
      'ผู้มีจิตกล้า หินระฆังสอนให้กายแน่นดุจเนื้อระฆัง ใจนิ่งดุจเสียงที่ยังก้องอยู่ หากเข้าสำนักจะได้รับหมัดระฆังก้องและคัมภีร์หลอมกายระฆังทันที เจ้าจะเข้าสำนักหรือไม่',
    memberText:
      'ศิษย์หินระฆัง จงฝึกกายให้แน่นดุจระฆังแล้วศัตรูจะทำอะไรเจ้าไม่ได้ เมื่อแต้มฝึกปรือพอ เปิดแผงสำนัก (J) เรียนฝ่ามือก้องระฆัง แล้วจึงถึงดาบค้อนช่างใหญ่',
  },
  {
    id: 'lantern-road',
    phase: 'launch',
    name: 'ทางโคมเหล็ก',
    motto: 'กฎต้องมีใจ ดาบต้องมีด้าม',
    desc: 'ผู้ดูแลโคมประจำทางค้าและลานประลอง ไม่รับค่าจ้างจากราชสำนักใด นักเดินทางทิ้งน้ำมันโคมไว้ให้ ใช้หัตถ์ล็อกโซ่ ดาบชักเร็ว และวางโคมเป็นเขตเผยตัวศัตรู คุมเกมมากกว่าเอาเลือด',
    discipleTitle: 'ศิษย์ทางโคมเหล็ก',
    arts: ['lamp-hook-sabre', 'chain-hold', 'quick-draw-sabre'],
    neigong: 'road-iron',
    uniform: 'heroJinyiwei',
    master: { name: 'ผู้คุมทางหลู่เจิ้ง', title: 'ผู้แทนทางโคมเหล็ก', sprite: 'guardOfficer' },
    post: { dx: -0.6, dy: -4.6 },
    recruit: { dx: -1.4, dy: 1 },
    joinText:
      'ทางค้าและลานประลองต้องการคนกล้า หากรับตำแหน่งผู้ดูแลทางโคมเหล็ก เจ้าจะได้รับดาบขอเกี่ยวโคมและกายาเหล็กทางโคมทันที รับตำแหน่งหรือไม่',
    memberText:
      'ศิษย์ทางโคมเหล็ก กฎคือแสงที่ถือไปไม่ใช่รั้ว จับผู้ผิดให้ถูกจังหวะแล้วจะได้ถือดาบชักเร็วโคมเหล็ก (แผงสำนัก J)',
  },
  {
    id: 'pine-ink',
    phase: 'launch',
    name: 'หอหมึกสน',
    motto: 'หนึ่งขีดที่แม่น ดีกว่าร้อยขีดที่เร็ว',
    desc: 'หอบันทึกประลอง ผู้เฝ้ากระบี่พู่กัน ช่างถูศิลาและช่างทำหมึกเขม่าสน จดทุกการประลองในยุทธภพและตัดสินลานประลองตามเทศกาล แม่นยำ ปิดจุดชีพจร และลงโทษท่าที่เปิดเผยจังหวะ',
    discipleTitle: 'ศิษย์หอหมึกสน',
    arts: ['ink-slate', 'pulse-seal', 'one-cut'],
    neigong: 'ink-breath',
    uniform: 'heroJunzitang',
    master: { name: 'ผู้จดเหวินชิง', title: 'ผู้แทนหอหมึกสน', sprite: 'scholar' },
    post: { dx: -4.6, dy: 2.4 },
    recruit: { dx: 4.2, dy: 1 },
    joinText:
      'สหายผู้ใฝ่รู้ หอหมึกสนเชื่อว่าทุกการประลองควรมีผู้จดจำ และหนึ่งขีดที่แม่นชนะร้อยขีดที่เร็ว หากเข้าสำนักจะได้รับกระบี่แท่งหมึกสนและลมปราณหมึกสนทันที ท่านจะเข้าร่วมหรือไม่',
    memberText:
      'ปิดจุดชีพจรก่อนศัตรูร่ายท่าใหญ่ แล้วใช้สมาธิหมึกนิ่งปิดฉากอย่างสง่างาม กระบี่ปลายแท่งหมึกและขีดเดียวไร้ถ้อยเรียนต่อได้ในแผงสำนัก (J)',
  },
  {
    id: 'cinnabar',
    phase: 'launch',
    name: 'ดาบชาด',
    motto: 'คมที่ไม่หยุด คือคมที่ยังร้อน',
    desc: 'สำนักช่างตีดาบแห่งผาแดง ผู้ดึงกระบี่ขึ้นจากผา ตีดาบในไฟสีชาดแล้วแบกไว้กลางหลัง บุกไม่หยุด สะสมความร้อนให้ดาบเร็วและแรงขึ้น แต่ไฟนั้นแผดเผาการ์ดของตนเอง',
    discipleTitle: 'ศิษย์สำนักดาบชาด',
    arts: ['heat-sabre', 'tongs-claw', 'cliff-push-sabre'],
    neigong: 'ember-heart',
    uniform: 'heroBlood',
    master: { name: 'ประมุขรองเสวี่ยอู๋', title: 'ผู้แทนสำนักดาบชาด', sprite: 'bloodMaster' },
    post: { dx: -4.6, dy: -3.6 },
    recruit: { dx: 7, dy: 1 },
    joinText:
      'เจ้ากล้ามองไฟในตัวเองหรือไม่ สำนักดาบชาดไม่รับคนขลาด หากเข้าสำนัก เจ้าจะได้รับวิชาดาบชาดคลั่งไฟและคัมภีร์คมชาดทันที เข้าหรือไม่',
    memberText:
      'ให้ศัตรูเลือดไหลก่อน แล้วดื่มไฟศัตรู เจ้าจะไม่มีวันล้ม คีมเล็บพยัคฆ์ไฟและดาบผลักผาชาดรอเจ้าในแผงสำนัก (J)',
  },
  {
    id: 'dim-moon',
    phase: 'launch',
    name: 'จันทร์มัว',
    motto: 'อย่าชักกระบี่ก่อนเห็นใจคน',
    desc: 'หุบผู้ขับกล่อมคนตายแห่งหุบจันทร์มัว ผู้เฝ้ากระบี่จันทร์เสน่หา นักดนตรีงานศพและผู้เฝ้าคืนที่เดินสนามรบและหมู่บ้านโรคระบาด ใช้ระฆังมือ ขลุ่ยกระดูก กลอง และพิณ ฟื้นพลังพวกพ้อง กดจังหวะศัตรู และอ่านท่าถัดไปของศัตรู',
    discipleTitle: 'ศิษย์หุบจันทร์มัว',
    arts: ['quiet-palm', 'listening-blade', 'chorus'],
    neigong: 'mourning-heart',
    uniform: 'heroJilegu',
    master: { name: 'ผู้อาวุโสแห่งคืนเฝ้าเงาเหมย', title: 'ผู้แทนหุบจันทร์มัว', sprite: 'blissLady' },
    post: { dx: 4.4, dy: 1.4 },
    recruit: { dx: 1.4, dy: 1 },
    joinText:
      'ผู้ลงนามสัญญาเลือดหรือ หุบจันทร์มัวไม่ได้เล่นให้คนดู แต่เล่นให้ผู้จากไปและผู้ที่ยังอยู่หลับได้ เข้ามาแล้วจะได้ฝ่ามือเงียบจันทร์มัวและจิตไว้อาลัยจันทร์มัว จะมาไหม',
    memberText:
      'ฟังก่อนชัก เมื่อจบทุกศึกต้องมีความเงียบ ดูแลมันให้ดี กระบี่ฟังใจจันทร์มัวและเพลงหมู่เงาจันทร์รอเจ้าอยู่ในแผงสำนัก (J)',
  },
  {
    id: 'snow-pine',
    phase: 'later',
    name: 'ตำหนักสนหิมะ',
    motto: 'ผู้ที่ขยับก่อนเสียความอุ่นก่อน',
    desc: 'ลานพรานตัดไม้และนายพรานดักสัตว์แห่งยอดเขาหิมะ ผู้เฝ้ากระบี่สนหิมะ ฝึกความอดทนและกับดัก ยิ่งยืนนิ่งยิ่งแรงและไกล วางบ่วงและเขตหิมะให้ศัตรูช้าลง',
    discipleTitle: 'ศิษย์ตำหนักสนหิมะ',
    arts: [],
    neigong: 'snow-hush',
    uniform: 'heroWudang',
    master: { name: 'พรานเฒ่าสนขาว', title: 'ผู้เฝ้าลานสนรอ', sprite: 'taoist' },
    post: { dx: 0, dy: 0 },
    recruit: { dx: 0, dy: 1 },
    joinText: 'ตำหนักสนหิมะสอนให้รอ หากเข้าสำนัก เจ้าจะได้ปราณหิมะสงัดทันที เจ้าจะเข้าสำนักหรือไม่',
    memberText: 'ศิษย์ตำหนักสนหิมะ ยืนนิ่งไว้ให้ศัตรูพลาดก่อน แล้วค่อยปล่อยแรง',
  },
  {
    id: 'broken-chain',
    phase: 'later',
    name: 'โซ่แตก',
    motto: 'ผู้แบกโซ่ได้ แบกแผ่นดินได้',
    desc: 'พี่น้องผู้ตื่นขึ้นในเหมืองเงาผาทั้งที่ถูกล่าม มีรอยตราเดียวกัน ไม่มีกระบี่ให้เฝ้า มีแต่ภาระให้แบก ฝึกคว้า ทุ่ม และอดทน ยิ่งโดนตียิ่งจับแน่น',
    discipleTitle: 'ศิษย์โซ่แตก',
    arts: [],
    neigong: 'blood-oath',
    uniform: 'heroWudang',
    master: { name: 'ผู้นำโซ่ขาด', title: 'ผู้แทนโซ่แตก', sprite: 'taoist' },
    post: { dx: 0, dy: 0 },
    recruit: { dx: 0, dy: 1 },
    joinText: 'โซ่แตกรับคนที่เคยถูกล่าม หากเข้าร่วม เจ้าจะได้พี่น้องที่แบกด้วยกัน เจ้าจะเข้าร่วมหรือไม่',
    memberText: 'ศิษย์โซ่แตก โดนตีเท่าไรก็อย่าปล่อยมือ',
  },
];

/** Every sect of the original roster, launch and later. */
export const SCHOOL_ROSTER: Readonly<Record<string, SchoolDef>> = Object.fromEntries(list.map((s) => [s.id, s]));

/**
 * The sects that can be joined now: those with their arts written. Rules, recruiters and tests iterate this;
 * a sect enters it the moment its `arts` are filled in (bamboo-tide is `launch` but waits for its three arts).
 */
export const SCHOOLS: Readonly<Record<string, SchoolDef>> = Object.fromEntries(
  list.filter((s) => s.arts.length > 0).map((s) => [s.id, s]),
);

export function getSchool(id: string): SchoolDef {
  const s = SCHOOL_ROSTER[id];
  if (!s) throw new Error(`Unknown school: ${id}`);
  return s;
}
